import type { AdvisorProfileViewProfile } from '@/shared/components/advisor-profile';
import type {
  AdminAdvisorEditorDetail,
  AdminAdvisorEditorUpdatePayload,
} from '../../../types/admin-advisor.type';
import type { AdvisorEditorFormData } from '../advisorEditor.types';

export const advisorDetailToForm = (
  advisor: AdminAdvisorEditorDetail,
): AdvisorEditorFormData => ({
  firstName: advisor.user.firstName,
  lastName: advisor.user.lastName ?? '',
  email: advisor.user.email,
  accountStatus: advisor.user.accountStatus ?? 'ACTIVE',
  profilePhoto: advisor.user.profilePhoto ?? '',
  companyName: advisor.companyName ?? '',
  partnerCompanyId: advisor.user.partnerCompany?.id ?? advisor.user.partnerCompanyId ?? null,
  companyOverview: advisor.companyOverview ?? '',
  // Feature 074 structured fields.
  serviceOfferingsList: advisor.serviceOfferingsList ?? [],
  predominantClientSegments: advisor.predominantClientSegments ?? [],
  investableAssets: advisor.investableAssets ?? '',
  legacyServiceOfferings: advisor.serviceOfferings ?? null,
  idealClientProfile: advisor.idealClientProfile ?? '',
  differentiatingFactors: advisor.differentiatingFactors ?? '',
  advisorBiography: advisor.advisorBiography ?? '',
  addressLine1: advisor.addressLine1 ?? '',
  addressLine2: advisor.addressLine2 ?? '',
  addressCity: advisor.addressCity ?? '',
  addressState: advisor.addressState ?? '',
  addressZip: advisor.addressZip ?? '',
  profilePrivate: advisor.profilePrivate,
  hideFromServiceProviders: advisor.hideFromServiceProviders,
  firmSizeSegment: advisor.firmSizeSegment ?? '',
  isFirmChampion: advisor.isFirmChampion ?? false,
});

const optionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const advisorFormToPayload = (
  formData: AdvisorEditorFormData,
): AdminAdvisorEditorUpdatePayload => {
  const trimmedState = formData.addressState.trim();

  return {
    firstName: formData.firstName.trim(),
    lastName: optionalText(formData.lastName),
    email: formData.email.trim(),
    accountStatus: formData.accountStatus,
    profilePhoto: optionalText(formData.profilePhoto),
    companyName: formData.companyName.trim(),
    partnerCompanyId: formData.partnerCompanyId,
    companyOverview: optionalText(formData.companyOverview),
    // Feature 074: always send client segments (empty array clears them) and investable
    // assets (value or null, so clearing the dropdown clears the field). The offerings
    // list is only sent when set, so an untouched legacy advisor keeps their free-text
    // offerings (sending the list clears legacy text).
    predominantClientSegments: formData.predominantClientSegments,
    investableAssets: formData.investableAssets || null,
    ...((formData.legacyServiceOfferings === null || formData.serviceOfferingsList.length > 0)
      ? { serviceOfferingsList: formData.serviceOfferingsList }
      : {}),
    idealClientProfile: optionalText(formData.idealClientProfile),
    differentiatingFactors: optionalText(formData.differentiatingFactors),
    advisorBiography: optionalText(formData.advisorBiography),
    addressLine1: optionalText(formData.addressLine1),
    addressLine2: optionalText(formData.addressLine2),
    addressCity: optionalText(formData.addressCity),
    addressState: trimmedState.length > 0 ? trimmedState.toUpperCase() : null,
    addressZip: optionalText(formData.addressZip),
    profilePrivate: formData.profilePrivate,
    hideFromServiceProviders: formData.hideFromServiceProviders,
    // Feature 071: only send a segment when one is selected; the backend
    // enforces "required on save" and rejects an edit that leaves it unset.
    firmSizeSegment: formData.firmSizeSegment || undefined,
    isFirmChampion: formData.isFirmChampion,
  };
};

export const advisorFormToPreviewProfile = (
  advisor: AdminAdvisorEditorDetail,
  formData: AdvisorEditorFormData,
): AdvisorProfileViewProfile => ({
  id: advisor.id,
  user: {
    id: advisor.user.id,
    email: formData.email,
    firstName: formData.firstName,
    lastName: optionalText(formData.lastName),
    profilePhoto: optionalText(formData.profilePhoto),
    accountStatus: formData.accountStatus,
  },
  companyName: optionalText(formData.companyName),
  companyOverview: optionalText(formData.companyOverview),
  // Feature 074: structured list wins over legacy text for the preview.
  serviceOfferings:
    formData.serviceOfferingsList.length > 0 ? null : optionalText(formData.legacyServiceOfferings ?? ''),
  serviceOfferingsList: formData.serviceOfferingsList,
  predominantClientSegments: formData.predominantClientSegments,
  investableAssets: formData.investableAssets || null,
  idealClientProfile: optionalText(formData.idealClientProfile),
  differentiatingFactors: optionalText(formData.differentiatingFactors),
  advisorBiography: optionalText(formData.advisorBiography),
  addressLine1: optionalText(formData.addressLine1),
  addressLine2: optionalText(formData.addressLine2),
  addressCity: optionalText(formData.addressCity),
  addressState: formData.addressState.trim() ? formData.addressState.trim().toUpperCase() : null,
  addressZip: optionalText(formData.addressZip),
  profileComplete: advisor.profileComplete,
  missingRequiredFields: advisor.missingRequiredFields,
});
